import { CombinedGraphQLErrors } from '@apollo/client/errors'

import { client } from '~publish/legacy/apollo-client/index'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'

import type { AutocompleteItem } from '../../autocomplete'
import {
  LINKEDIN_ENTITY_URL_REGEX,
  LINKEDIN_PROFILE_URL_REGEX,
} from '../LinkedInAnnotations'
import { getRecentItems } from '../queries/recentSuggestions'

const GetServiceMentions = graphql(/* GraphQL */ `
  query GetServiceMentions($channelIds: [ChannelId!]!, $query: String!) {
    serviceMentions(input: { channelIds: $channelIds, query: $query }) {
      displayName
      link
      id
      entity
      vanityName
      image
    }
  }
`)

type ServiceMention = ResultOf<
  typeof GetServiceMentions
>['serviceMentions'][number]

export function parseSuggestions(
  suggestions: ServiceMention[],
): AutocompleteItem[] {
  return suggestions.map((suggestion) => ({
    key: suggestion.entity,
    text: suggestion.displayName,
    data: {
      id: suggestion.id,
      entity: suggestion.entity,
      name: suggestion.displayName,
      vanityName: suggestion.vanityName,
      localizedName: suggestion.displayName,
      avatar: suggestion.image ?? undefined,
      link: suggestion.link,
    },
  }))
}

export type LinkedInMentionSearchError =
  | { type: 'permission'; channelId: string }
  | { type: 'generic' }

/**
 * Fetches new LinkedIn mention suggestions for the given search text
 * (recent items for an empty query, otherwise a real search — LinkedIn
 * URL-paste resolution is a separate concern, handled by
 * resolveLinkedInEntityUrl since it isn't a text search at all). Throws a
 * LinkedInMentionSearchError on failure for the caller to render.
 */
export async function getNewSuggestionsOnMentionUpdate(
  editor: BufferEditor,
  searchText: string,
): Promise<AutocompleteItem[]> {
  const editorComposerData = editor.getEditorComposerData?.(editor)
  const channelIds = editorComposerData?.selectedProfilesForService.map(
    (profile) => profile.id,
  )

  if (!channelIds?.length) return []

  if (searchText.length === 0) {
    return getRecentItems() ?? []
  }

  // Entity URLs (company/school/showcase) resolve directly via
  // resolveLinkedInEntityUrl, not a text search — searching on the raw URL
  // string would just be a wasted request racing that resolution.
  if (LINKEDIN_ENTITY_URL_REGEX.test(searchText)) return []

  // A pasted LinkedIn profile URL isn't resolved directly — unlike a
  // company/school/showcase URL, it doesn't map to one specific entity, so
  // it just seeds a real search with the profile's vanity name.
  const profileVanityName = searchText.match(LINKEDIN_PROFILE_URL_REGEX)?.groups
    ?.vanityName

  // Any other URL-shaped paste (not a profile, not an entity) has nothing
  // to search on — LinkedInUrlPasteResolver already reports it as not
  // found, so don't also fire a search against the raw URL string.
  if (profileVanityName === undefined && /^https?:\/\//.test(searchText)) {
    return []
  }

  const query = profileVanityName ?? searchText

  if (query.length < 3) {
    return []
  }

  try {
    const { data } = await client.query({
      query: GetServiceMentions,
      variables: {
        channelIds,
        query,
      },
    })

    if (data?.serviceMentions) {
      return parseSuggestions(data.serviceMentions)
    }
  } catch (error) {
    if (
      CombinedGraphQLErrors.is(error) &&
      (error.message.includes('status code 403') ||
        error.message.includes(
          'LinkedIn API returned an unauthorized response',
        ))
    ) {
      const errorChannelId = error.errors[0]?.extensions?.channelId
      throw {
        type: 'permission',
        channelId:
          typeof errorChannelId === 'string' ? errorChannelId : channelIds[0],
      } satisfies LinkedInMentionSearchError
    }
    throw { type: 'generic' } satisfies LinkedInMentionSearchError
  }

  return []
}
